import React from "react";
import { useMap } from "maplibre-react-components";
import { motion, AnimatePresence } from "framer-motion";
import cn from "classnames";
import type { Map as MapLibreMap } from "maplibre-gl";
import { postGeoPinClickEvent } from "@/api/analytics";
import { YaMapsIcon } from "@/assets/icons";
import { useTariffLayer } from "@/store/mode";
import Typography from "@/ui/Typography/Typography";
import { makeNavigationUrl } from "@/utils/helpers";
import { useCurrentPriceRange } from "@/utils/hooks/useCurrentPriceRange";
import { usePinPrice } from "@/utils/hooks/usePinPrice";
import { useIsDraggingMap } from "@/utils/hooks/useIsDraggingMap";
import { useTranslation } from "react-i18next";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";
import cls from "./styles.module.scss";

type Props = { mapId: string };

export const BoardContent = ({ price }: { price: number | null }) => {
  const { t } = useTranslation();
  const currentPriceRange = useCurrentPriceRange();
  const tariffLayer = useTariffLayer((s) => s.getLayer());

  const textVariants = {
    initial: { width: 0, opacity: 0 },
    enter: {
      width: "auto",
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" },
    },
    exit: {
      width: 0,
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const { pin } = getTariffModeConfig(tariffLayer);
  const coefficient = (() => {
    if (price === null) return null;
    if (!pin.subtractMin) return price;

    return price - (currentPriceRange?.min || 0);
  })();

  return (
    <AnimatePresence>
      {price !== null && (
        <motion.div
          key="coef-text"
          className={cls.text}
          initial="initial"
          animate="enter"
          exit="exit"
          variants={textVariants}
          style={{ overflow: "hidden", whiteSpace: "nowrap" }}
        >
          <Typography className={cls.price}>
            {coefficient !== 0 && pin.prefix}
            {coefficient}₽{pin.suffix}
          </Typography>
          <Typography className={cls.openIn}>{t("common.openIn")}</Typography>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const BoardYaMapsButton = () => {
  return (
    <motion.div
      key="ya-maps"
      className={cls.icon}
      initial={{ scale: 0.8, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <YaMapsIcon />
    </motion.div>
  );
};

export default function CenterSign({ mapId }: Props) {
  const map = useMap(mapId) as MapLibreMap | null;
  const price = usePinPrice(mapId);
  const isDraggingMap = useIsDraggingMap(mapId);
  const serviceLayer = useTariffLayer((s) => s.serviceLayer);

  const handleNavigate = () => {
    if (!map) return;

    const service_type = serviceLayer === "taxi" ? "taxi" : "eda";
    postGeoPinClickEvent({ service_type }).catch((error) => {
      //
    });

    const { lat, lng } = map.getCenter();
    const url = makeNavigationUrl(lat, lng);
    window.open(url, "_blank", "noopener noreferer");
  };

  return (
    <div
      id="pinStep"
      className={cn(cls.container, { [cls.dragging]: isDraggingMap })}
    >
      <button type="button" onClick={handleNavigate} className={cls.board}>
        <BoardContent price={price} />
        <BoardYaMapsButton />
      </button>
      <div className={cls.stick} />
    </div>
  );
}
